import { useEffect, useState, type DependencyList } from 'react';

export type ResourceStatus = 'loading' | 'success' | 'empty' | 'error';

export interface ResourceState<Value> {
  readonly data: Value | null;
  readonly error: unknown | null;
  readonly status: ResourceStatus;
}

export type ResourceLoader<Value> = (signal: AbortSignal) => Promise<Value>;

export function useResource<Value>(
  load: ResourceLoader<Value>,
  dependencies: DependencyList,
  isEmpty: (value: Value) => boolean = defaultIsEmpty,
): ResourceState<Value> {
  const [state, setState] = useState<ResourceState<Value>>({
    data: null,
    error: null,
    status: 'loading',
  });

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setState({ data: null, error: null, status: 'loading' });

    let request: Promise<Value>;
    try {
      request = Promise.resolve(load(controller.signal));
    } catch (error) {
      request = Promise.reject(error);
    }

    void request
      .then((data) => {
        if (!active || controller.signal.aborted) return;
        const status = isEmpty(data) ? 'empty' : 'success';
        setState({ data, error: null, status });
      })
      .catch((error: unknown) => {
        if (!active || controller.signal.aborted) return;
        setState({ data: null, error, status: 'error' });
      });

    return () => {
      active = false;
      controller.abort();
    };
    // The caller explicitly owns the dependency list so feature queries stay visible at call sites.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  return state;
}

function defaultIsEmpty(value: unknown): boolean {
  return value === null || value === undefined || (Array.isArray(value) && value.length === 0);
}
